import React, { useState } from 'react';
import {
    Show,
    TextField,
    SimpleShowLayout,
    TabbedShowLayout,
    Tab,
    ReferenceField,
    ChipField,
    ImageField,
    DateField,
    Button,
    FunctionField,
} from 'react-admin';
import { connect } from 'react-redux';
import { showNotification } from 'ra-core';
import { getUserURL } from '../api';
import { API_BASE } from '../dataProvider';

const ImageModal = ({ src, alt, onClose }) => (
    <div 
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            cursor: 'pointer'
        }}
        onClick={onClose}
    >
        <img 
            src={src} 
            alt={alt}
            style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain'
            }}
            onClick={e => e.stopPropagation()}
        />
    </div>
);

// 复制密码初始链接按钮组件
const CopyPasswordInitialLinkButton = ({ email, dispatch }) => {
    const handleCopyPasswordInitialLink = async (e) => {
        e.stopPropagation(); // 阻止事件冒泡
        try {
            // 从localStorage获取token，参考dataProvider的实现
            const token = localStorage.getItem('BITFA:DASHBOARD:AUTH:TOKEN');
            const headers = new Headers({ 
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
            });
            
            const response = await fetch(`${API_BASE}/password-reset/copy?email=${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: headers,
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.link) {
                    // 复制链接到剪贴板
                    await navigator.clipboard.writeText(data.link);
                    dispatch(showNotification('Password initial link copied to clipboard successfully', 'info'));
                } else {
                    dispatch(showNotification('No link returned from server', 'warning'));
                }
            } else {
                dispatch(showNotification('Failed to get password initial link, please try again later', 'warning'));
            }
        } catch (error) {
            console.error('Error copying password initial link:', error);
            dispatch(showNotification('Failed to copy link, please try again later', 'warning'));
        }
    };
    
    return (
        <Button 
            label="Copy Password Initial Link" 
            onClick={handleCopyPasswordInitialLink}
            variant="contained"
            size="small"
            color="secondary"
            style={{
                backgroundColor: '#f50057',
                color: 'white',
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '12px',
                padding: '4px 12px',
                minWidth: 'auto',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginTop: '16px',
                '&:hover': {
                    backgroundColor: '#c51162',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }
            }}
        />
    );
};

// 连接Redux的按钮组件
const ConnectedCopyPasswordInitialLinkButton = connect()(CopyPasswordInitialLinkButton);

// 包装组件，用于在Show页面中获取record数据，并进行权限控制
const CopyPasswordInitialLinkField = ({ record, permissions }) => {
    // 只有system_admin或promoter权限才显示复制密码初始链接按钮
    if (!permissions || (permissions !== "system_admin" && permissions !== "promoter")) {
        return null;
    }
    
    if (!record || !record.email) {
        return null;
    }
    
    return (
        <div style={{ marginTop: '16px' }}>
            <ConnectedCopyPasswordInitialLinkButton email={record.email} />
        </div>
    );
};

const CustomImageField = ({ record, source, label }) => {
    const [showModal, setShowModal] = useState(false);
    
    const imageUrl = record && record[source] ? `${getUserURL()}/${record[source]}` : null;

    return (
        <div style={{ marginBottom: '1rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ 
                    display: 'block', 
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontSize: '0.8rem'
                }}>
                    {label}
                </label>
            </div>
            {imageUrl ? (
                <div 
                    style={{ 
                        cursor: 'pointer',
                        display: 'inline-block'
                    }}
                    onClick={() => setShowModal(true)}
                >
                    <img 
                        src={imageUrl}
                        alt={label}
                        style={{ 
                            maxWidth: '150px',
                            maxHeight: '150px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0'
                        }}
                    />
                </div>
            ) : (
                <div>-</div>
            )}
            {showModal && imageUrl && (
                <ImageModal 
                    src={imageUrl}
                    alt={label}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

const CustomDateField = ({ record, source, label }) => {
    return (
        <div style={{ marginBottom: '0.1rem' }}>
            <div style={{ 
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '0.8rem',
                marginBottom: '0.5rem'
            }}>
                {label}
            </div>
            <div>
                {(!record || !record[source] || isNaN(new Date(record[source]).getTime())) 
                    ? '-'
                    : new Date(record[source]).toLocaleDateString()
                }
            </div>
        </div>
    );
};

const SignOnBonusField = ({ record }) => {
    if (record.userType !== "promoter") {
        return null;
    }
    return (
        <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ 
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '0.8rem',
                marginBottom: '0.5rem'
            }}>
                Sign On Bonus Amount
            </div>
            <div>{record.signOnBonusAmount}</div>
        </div>
    )
}

const RevenueSharesField = ({record}) => {
    if (record.userType !== "promoter") {
        return null;
    }
    let revenueShares = record.revenueShares?record.revenueShares:[];
    return (
        <div>
            <div style={{ 
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '0.8rem',
                marginBottom: '0.5rem'
            }}>Revenue Share settings</div>
            { revenueShares.map((share, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <div style={{ marginRight: '20px' }}>
                        <strong>Month:</strong> {share.month}
                    </div>
                    <div>
                        <strong>Rate:</strong> {share.rate}%
                    </div>
                </div>
            ))}
        </div>
    );
};

const UserShow = ({ permissions, ...props }) => (
    <Show title="User Info" {...props}>
        <TabbedShowLayout>
            <Tab label="Base Information">
                <TextField source="name" label="Username" />
                <TextField source="firstName" label="First Name" />
                <TextField source="lastName" label="Last Name" />
                <TextField source="middleName" label="Middle Name" />
                <TextField source="email" label="Email" />
                <FunctionField 
                label="Account Number" 
                render={record => {
                    if (record.accounts && record.accounts.accountNumber) {
                        return (
                            <a 
                                href={`#/accounts?filter={"account_number":"${record.accounts.accountNumber}"}`}
                                style={{ textDecoration: 'none', color: '#1976d2' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                {record.accounts.accountNumber}
                            </a>
                        );
                    }
                    return '-';
                }}
            />
                <TextField source="phoneNumber" label="Phone Number" />
                <TextField source="country" label="Country" />
                <CustomDateField source="dateOfBirth" label="Date of Birth" />
                <TextField source="userType" label="User Type" />
                {/*<TextField source="ssn" label="Social Security Number (SSN or ITIN)" />*/}
                <SignOnBonusField/>
                <RevenueSharesField/>
                {/* 复制密码初始链接按钮 - 只有system_admin或promoter权限才显示 */}
                <CopyPasswordInitialLinkField permissions={permissions}/>
            </Tab>
            <Tab label="Home Mailing Address">
                <TextField source="address.address2" label="Unit Number" />
                <TextField source="address.address1" label="Street Address" />
                <TextField source="address.city" label="City" />
                <TextField source="address.state" label="State" />
                <TextField source="address.postalCode" label="Postal Code" />
                <TextField source="address.country" label="Country" />
            </Tab>
        </TabbedShowLayout>
    </Show>
);

export default UserShow;
